import { Form, Head, usePage } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";

import GoogleSignInButton from "@/components/google-sign-in-button";
import InputError from "@/components/input-error";
import TextLink from "@/components/text-link";
import TurnstileWidget from "@/components/turnstile-widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFaviconUrl } from "@/hooks/use-favicon-url";
import { useLeadDomain } from "@/hooks/use-lead-domain";
import AuthLayout from "@/layouts/auth-layout";
import { signInPath, signUpPath } from "@/routes";

interface RegisterProps {
  turnstile_site_key?: string | null;
}

export default function Register({ turnstile_site_key }: RegisterProps) {
  const { getLeadDomain } = useLeadDomain();
  const [leadDomain, setLeadDomain] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRequired = Boolean(turnstile_site_key);
  const { errors } = usePage<{ errors?: Record<string, string> }>().props;
  const faviconUrl = useFaviconUrl(leadDomain);

  useEffect(() => {
    const domain = getLeadDomain();
    setLeadDomain(domain);
  }, [getLeadDomain]);

  return (
    <AuthLayout title="Create an account" description="Enter your details below to create your account">
      <Head title="Register" />

      {leadDomain ? (
        <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-4 text-sm text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-100">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase">Connected domain</p>
          <div className="mt-2 flex items-center gap-3 font-medium">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-white/80 shadow">
              {faviconUrl ? (
                <img src={faviconUrl} alt={`${leadDomain} favicon`} className="h-6 w-6 rounded" />
              ) : (
                <span className="h-6 w-6 text-lg">🌐</span>
              )}
            </div>
            <span>{leadDomain}</span>
          </div>
          <p className="mt-1 text-xs text-orange-600 dark:text-orange-200">
            You can change this later from the onboarding flow.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-6">
        <TurnstileWidget
          siteKey={turnstile_site_key ?? ""}
          action="sign_up"
          onTokenChange={setTurnstileToken}
          error={errors?.turnstile}
        />
        <GoogleSignInButton
          label="Sign up with Google"
          actionPath="/sign_up/google"
          turnstileToken={turnstileToken}
          turnstileRequired={turnstileRequired}
          leadDomain={leadDomain}
        />

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="text-muted-foreground bg-background px-2">Or continue with</span>
          </div>
        </div>

        <Form
          method="post"
          action={signUpPath()}
          resetOnSuccess={["password"]}
          disableWhileProcessing
          className="flex flex-col gap-6"
        >
          {({ processing, errors }) => (
            <>
              <div className="grid gap-6">
                <input type="hidden" name="lead_domain" value={leadDomain ?? ""} />
                <input type="hidden" name="cf-turnstile-response" value={turnstileToken} />

                <div className="grid gap-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    required
                    autoFocus
                    tabIndex={1}
                    autoComplete="email"
                    disabled={processing}
                    placeholder="email@example.com"
                  />
                  <InputError message={errors.email} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    required
                    tabIndex={2}
                    autoComplete="new-password"
                    placeholder="Password (min 8 characters)"
                  />
                  <InputError message={errors.password} />
                </div>

                <Button
                  type="submit"
                  className="mt-2 w-full"
                  tabIndex={3}
                  disabled={processing || (turnstileRequired && !turnstileToken)}
                >
                  {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  Create account
                </Button>
              </div>

              <div className="text-muted-foreground text-center text-sm">
                Already have an account?{" "}
                <TextLink href={signInPath()} tabIndex={4}>
                  Log in
                </TextLink>
              </div>
            </>
          )}
        </Form>
      </div>
    </AuthLayout>
  );
}
